import { useErrorInlineNotificationAPI } from '@/components/ErrorInlineNotification/ErrorInlineNotification';
import { useUpdateReceivedInvoice } from '@/context/services/invoice/ReceivedInvoice.service';
import { UpdateReceivedInvoiceErrorResponse } from '@/context/services/invoice/type';
import { useIsView } from '@/features/invoices/components/Details/Components/Context/ViewEdit';
import { ReceivedInvoiceForm } from '@/features/invoices/components/Details/Components/ReceivedInvoice/types';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import { useTranslation } from '@/i18n';
import { isResponseError } from '@/libs/typeguard/isError';
import { SendErrorTracking } from '@/utils/errors';
import { useNotification } from '@moneyforward/ap-frontend-components';
import { useQueryClient } from '@tanstack/react-query';
import { getGetEDocPlanQueryKey } from 'ap-openapi';
import { toISODateString } from 'date-util';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { SubmitHandler, UseFormSetError } from 'react-hook-form';

export const useSubmit = (
  id: string,
  setError: UseFormSetError<ReceivedInvoiceForm>,
  setPicError: Dispatch<SetStateAction<string[]>>
) => {
  const setErrorNotification = useErrorInlineNotificationAPI();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const notify = useNotification();
  const { onView } = useIsView();
  const [isLoading, setLoading] = useState(false);
  const { mutateAsync } = useUpdateReceivedInvoice(id);
  const flagEDocMetadata = useGetFlags('E_DOC_METADATA');
  const onSubmit: SubmitHandler<ReceivedInvoiceForm> = useCallback(
    async (form) => {
      setLoading(true);
      try {
        const resp = await mutateAsync({
          id: id,
          data: {
            received_invoice: {
              ap_payee_id: form.apPayeeId,
              memo: form.memo,
              pic_ids: form.formPics,
              receipt_type: form.receiptType!,
              book_date: flagEDocMetadata
                ? form.bookDate
                  ? toISODateString(form.bookDate)
                  : null
                : undefined,
            },
          },
        });

        if (resp.status === 'success') {
          queryClient.invalidateQueries({
            queryKey: getGetEDocPlanQueryKey(),
          });

          notify.success({
            title: t('受取り情報を更新しました'),
            opts: {
              duration: 3000,
              style: {
                position: 'fixed',
              },
            },
            bodyProps: {
              disabledSuffix: true,
            },
          });

          onView();
        } else if (resp.status === 'error') {
          setErrorNotification(new Error(resp.errors.messages?.join('\n')));
        } else {
          if (
            isResponseError<UpdateReceivedInvoiceErrorResponse>(
              resp.errors,
              'errors'
            )
          ) {
            Object.entries(resp.errors.errors ?? {}).forEach(
              ([key, values]) => {
                values.forEach((value) => {
                  const message = value.message;
                  switch (key) {
                    case 'memo':
                      setError('memo', {
                        type: 'validate',
                        message: message,
                      });
                      return;
                    case 'pic_ids':
                      if (value.code === 'not_found') {
                        const v = value.value;
                        if (Array.isArray(v)) {
                          setPicError((old) => [...old, ...v]);
                        }
                      }
                      setError('formPics', {
                        type: 'validate',
                        message: message,
                      });
                      return;
                    case 'receipt_type':
                      setError('receiptType', {
                        type: 'validate',
                        message: message,
                      });
                      return;
                    case 'status':
                      setErrorNotification(new Error(message));
                      return;
                    case 'ap_payee_id':
                      setError('apPayeeId', {
                        type: 'validate',
                        message: message,
                      });
                      return;
                    case 'book_date':
                      setError('bookDate', {
                        type: 'validate',
                        message: message,
                      });
                      return;
                    default:
                      return;
                  }
                });
              }
            );
          }
        }
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        setErrorNotification(new SendErrorTracking(err as Error));
      } finally {
        setLoading(false);
      }
    },
    [
      id,
      mutateAsync,
      notify,
      onView,
      queryClient,
      setError,
      setErrorNotification,
      setPicError,
      t,
      flagEDocMetadata,
    ]
  );
  return {
    isLoading,
    onSubmit,
  };
};
